import { Box, Stack, Tag, Text } from '@chakra-ui/react'
import { faClock } from '@fortawesome/pro-regular-svg-icons'
import { faCheck, faHand, faVideo, type IconDefinition } from '@fortawesome/pro-solid-svg-icons'

import type { PostStatus } from '@/client/services/worlds'

import Icon from '@/components/UI/Icon/Icon'
import type { LiveHelpRequestBase } from '@/components/Worlds/types'

type LiveHelpStatus = 'awaiting-live-help' | 'receiving-live-help'

type TagDisplayInfo = { icon: IconDefinition; label: string; color: string; bgColor: string }

const POST_STATUS_TO_DISPLAY_INFO: Record<PostStatus, TagDisplayInfo> = {
  'not-solved': {
    icon: faHand,
    label: 'Not Solved',
    color: 'orange.fg',
    bgColor: 'yellow.subtle',
  },
  solved: {
    icon: faCheck,
    label: 'Solved',
    color: 'green.fg',
    bgColor: 'green.subtle',
  },
  closed: {
    icon: faClock,
    label: 'Closed',
    color: 'fg.muted',
    bgColor: 'bg.muted',
  },
}

const LIVE_HELP_STATUS_TO_DISPLAY_INFO: Record<LiveHelpStatus, TagDisplayInfo> = {
  'awaiting-live-help': {
    icon: faVideo,
    label: 'Waiting for Zoom help',
    color: 'red.fg',
    bgColor: 'red.subtle',
  },
  'receiving-live-help': {
    icon: faVideo,
    label: 'Receiving Zoom help',
    color: 'blue.fg',
    bgColor: 'blue.subtle',
  },
}

function StatusTag({ displayInfo }: { displayInfo: TagDisplayInfo }): JSX.Element {
  const { icon, label, color, bgColor } = displayInfo

  return (
    <Box display="inline-flex">
      <Tag
        variant="solid"
        backgroundColor={bgColor}
        color={color}
        size="md"
        whiteSpace="nowrap"
        py="4px"
      >
        <Stack direction="row" spacing="10px" alignItems="center">
          <Icon size="sm" faIcon={icon} omitContainer />
          <Text fontSize="14px" lineHeight="20px" color={color}>
            {label}
          </Text>
        </Stack>
      </Tag>
    </Box>
  )
}

function getLiveHelpStatus(
  currentLiveHelpRequest: LiveHelpRequestBase | null
): LiveHelpStatus | null {
  if (currentLiveHelpRequest == null) {
    return null
  }

  if (currentLiveHelpRequest.sessionId == null) {
    return 'awaiting-live-help'
  }

  return 'receiving-live-help'
}

export default function PostStatusTags({
  status,
  currentLiveHelpRequest,
}: {
  status: PostStatus
  currentLiveHelpRequest: LiveHelpRequestBase | null
}): JSX.Element {
  const postStatusDisplayInfo = POST_STATUS_TO_DISPLAY_INFO[status]

  const liveHelpStatus = getLiveHelpStatus(currentLiveHelpRequest)
  const liveHelpStatusDisplayInfo =
    liveHelpStatus != null ? LIVE_HELP_STATUS_TO_DISPLAY_INFO[liveHelpStatus] : null

  return (
    <Stack direction={{ base: 'column', lg: 'row' }} spacing="4px" alignItems="flex-end">
      <StatusTag displayInfo={postStatusDisplayInfo} />
      {liveHelpStatusDisplayInfo != null ? (
        <StatusTag displayInfo={liveHelpStatusDisplayInfo} />
      ) : null}
    </Stack>
  )
}
